import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import PageLayout from "../components/admin/PageLayout";
import { resolveStorageUrl } from "../utils/storageUrl";

const EditCategory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    category_name: "",
    category_order: "",
    category_type: "",
    active: true,
  });
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    fetchCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCategory = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("category");

      if (response.data.success) {
        const category = response.data.data.find((cat) => cat.id === id);
        if (category) {
          setFormData({
            category_name: category.category_name,
            category_order: category.category_order,
            category_type: category.category_type,
            active: category.active,
          });
          setPreviewUrl(resolveStorageUrl(category.category_image));
        }
      }
    } catch (error) {
      console.error("Error fetching category:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const formDataToSend = new FormData();

      // Append all form fields
      Object.keys(formData).forEach((key) => {
        formDataToSend.append(key, formData[key]);
      });

      // Append image if changed
      if (image) {
        formDataToSend.append("category_image", image);
      }

      const response = await apiClient.put(`category/${id}`, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        navigate("/categories");
      }
    } catch (error) {
      console.error("Error updating category:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageLayout title="Edit Category" subtitle="Update category details and image.">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
          <CircularProgress size={48} sx={{ color: "primary.main" }} />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Edit Category" subtitle="Update category details and image.">
      <form onSubmit={handleSubmit}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            maxWidth: 600,
          }}
        >
          <TextField
            label="Category Name"
            name="category_name"
            value={formData.category_name}
            onChange={handleInputChange}
            required
            fullWidth
          />

          <TextField
            label="Order"
            name="category_order"
            type="number"
            value={formData.category_order}
            onChange={handleInputChange}
            required
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Category Type</InputLabel>
            <Select
              name="category_type"
              value={formData.category_type}
              onChange={handleInputChange}
              label="Category Type"
              required
            >
              <MenuItem value="income">Income</MenuItem>
              <MenuItem value="expenses">Expenses</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, active: e.target.checked }))
                }
                name="active"
              />
            }
            label="Active"
          />

          <Box>
            <input
              accept="image/*"
              type="file"
              id="image-upload"
              onChange={handleImageChange}
              style={{ display: "none" }}
            />
            <label htmlFor="image-upload">
              <Button variant="contained" component="span">
                Change Image
              </Button>
            </label>
            {previewUrl && (
              <Box sx={{ mt: 2 }}>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                  }}
                />
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
            <Button variant="outlined" onClick={() => navigate("/categories")}>
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </PageLayout>
  );
};

export default EditCategory;
